import React, { useEffect, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Grid } from '@mui/material';
import { NewDialog } from 'web-components';
import { compose } from 'redux';
import { getData } from '../../../../../redux/rootSelectors';
import { loadMachinesSensorsTreeView } from '../../../../../redux/filters/actions';
import { getMachineSensorsTreeView } from '../../../../../redux/filters/selectors';
import { createGraphsTemplate } from '../../../../../redux/graphTemplates/actions';
import { periodObject } from '../../../../../helpers/periodObject';
import { generateColor } from '../../../../../helpers/colorUtils';
import { Steps } from './components/Steps';
import { Step1 } from './components/Step1';
import { Step2 } from './components/Step2';
import { ActionBar } from './components/ActionBar';
import { filterTreeBySelections, getDialogFilterOptions, keepSameArrayIfUnchanged } from '../dialogTreeFilters';

const steps = ['Step1', 'Step2'];

export function AddNewTemplate({ machine: machineParam, open, handleCloseDialog }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const treeView = useSelector(compose(getData, getMachineSensorsTreeView));

  const [selectedProductLines, setSelectedProductLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [templateIdentification, setTemplateIdentification] = useState('');
  const [filterTime, setFilterTime] = useState(periodObject[0].value);
  const [description, setDescription] = useState('');
  const [accessType, setAccessType] = useState('ACCESS_TYPE_USER_ONLY');
  const [alertSelectSensors, setAlertSelectSensors] = useState(false);
  const [errors, setErrors] = useState({
    templateIdentification: '',
    description: ''
  });
  const didApplyInitialSelectionRef = useRef(false);
  const productionLines = treeView?.production_lines || [];
  const { productLineOptions, machineOptions, sensorOptions } = useMemo(
    () =>
      getDialogFilterOptions({
        productionLines,
        selectedProductLines,
        selectedMachines,
        t
      }),
    [productionLines, selectedProductLines, selectedMachines, t]
  );
  const filteredTreeData = useMemo(
    () =>
      filterTreeBySelections({
        productionLines,
        selectedProductLines,
        selectedMachines,
        selectedSensors
      }),
    [productionLines, selectedProductLines, selectedMachines, selectedSensors]
  );

  const resetAllStates = () => {
    didApplyInitialSelectionRef.current = false;
    setActiveStep(0);
    setSelectedProductLines([]);
    setSelectedMachines([]);
    setSelectedSensors([]);
    setSelectedItems([]);
    setTemplateIdentification('');
    setFilterTime(periodObject[0].value);
    setDescription('');
    setAccessType('ACCESS_TYPE_USER_ONLY');
    setErrors({});
    setAlertSelectSensors(false);
  };

  useEffect(() => {
    if (!open) {
      resetAllStates();
    }
  }, [open]);

  useEffect(() => {
    if (open && machineParam?.id && !didApplyInitialSelectionRef.current) {
      const preDefProdLineFilter = [{ title: machineParam.production_line, value: machineParam.production_line }];
      const preDefMachineFilter = [{ id: machineParam.id, title: machineParam.name, value: machineParam.name }];

      didApplyInitialSelectionRef.current = true;
      setSelectedProductLines(preDefProdLineFilter);
      setSelectedMachines(preDefMachineFilter);
    }
  }, [open, machineParam]);

  useEffect(() => {
    if (!open) {
      return;
    }

    dispatch(
      loadMachinesSensorsTreeView({
        productionLines: [],
        sensors: [],
        machineIds: []
      })
    );
  }, [dispatch, open]);

  useEffect(() => {
    if (!open || !productionLines.length) {
      return;
    }

    setSelectedProductLines(prevValue =>
      keepSameArrayIfUnchanged(
        prevValue,
        prevValue.filter(item => productLineOptions.some(option => option.value === item.value)),
        item => item.value
      )
    );
    setSelectedMachines(prevValue =>
      keepSameArrayIfUnchanged(
        prevValue,
        prevValue.filter(item => machineOptions.some(option => option.id === item.id)),
        item => item.id
      )
    );
    setSelectedSensors(prevValue =>
      keepSameArrayIfUnchanged(
        prevValue,
        prevValue.filter(item => sensorOptions.some(option => option.type === item.type)),
        item => item.type
      )
    );
  }, [open, productionLines.length, productLineOptions, machineOptions, sensorOptions]);

  const handleProductLineChange = (event, value) => {
    const previousValues = selectedProductLines
      .map(item => item.value)
      .sort()
      .join('|');
    const nextValues = value
      .map(item => item.value)
      .sort()
      .join('|');
    const didChangeToAnotherParent = value.length > 0 && previousValues !== nextValues;

    setSelectedProductLines(value);

    if (didChangeToAnotherParent) {
      setSelectedMachines([]);
      setSelectedSensors([]);
    }

    setErrors({});
  };

  const handleMachineChange = (event, newValue) => {
    const previousValues = selectedMachines
      .map(item => item.id)
      .sort()
      .join('|');
    const nextValues = newValue
      .map(item => item.id)
      .sort()
      .join('|');
    const didChangeToAnotherParent = newValue.length > 0 && previousValues !== nextValues;

    setSelectedMachines(newValue);

    if (didChangeToAnotherParent) {
      setSelectedSensors([]);
    }
  };

  const handleSensorChange = (event, newValue) => {
    setSelectedSensors(newValue);
  };

  const handleSelectionChange = items => {
    if (!items || !Array.isArray(items)) return;

    const flattenedChildren = items.flatMap(item =>
      (item.childrenNames || []).map(child => ({
        ...child,
        machineID: item.machineID,
        productLine: item.productLine
      }))
    );

    const extractedData = flattenedChildren.map((child, index) => ({
      machine_id: child.machineID,
      type: child.type,
      auto_scale: true,
      line_color: generateColor(index),
      line_style: 'LINE_STYLE_SOLID',
      line_thickness: 1,
      max_scale: child.max_scale || null,
      min_scale: child.min_scale || null,
      is_custom: child.is_custom || false,
      is_static: child.is_static || false
    }));

    setSelectedItems(extractedData);
  };

  const validateFields = () => {
    const validationErrors = {};
    if (!templateIdentification) {
      validationErrors.templateIdentification = t(
        'dialog.add_new_template.validation.template_identification_required'
      );
    }
    if (!description) {
      validationErrors.description = t('dialog.add_new_template.validation.description_required');
    }
    return validationErrors;
  };

  const handleInputChange = (fieldName, value) => {
    if (fieldName === 'templateIdentification') {
      setTemplateIdentification(value);
    } else if (fieldName === 'description') {
      setDescription(value);
    }

    setErrors(prevErrors => ({
      ...prevErrors,
      [fieldName]: ''
    }));
  };

  const handleNext = () => {
    if (selectedItems.length < 2) {
      setAlertSelectSensors(true);
    } else {
      setAlertSelectSensors(false);
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSave = () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const values = {
      favorite: false,
      machine_id: machineParam?.id,
      name: templateIdentification,
      description,
      period_type: filterTime,
      sensors: selectedItems,
      tmpl_access_type: accessType
    };
    dispatch(createGraphsTemplate(values));
    handleCloseDialog();
  };

  const content = (
    <Box sx={{ minWidth: { sm: '320px', md: '600px', lg: '800px' } }}>
      {alertSelectSensors && (
        <Grid item xs={12}>
          <Alert severity="error">{t('dialog.add_new_template.alert_to_select_sensors')}</Alert>
        </Grid>
      )}
      <Steps activeStep={activeStep} steps={steps} />
      {activeStep === 0 && (
        <Step1
          selectedProductLines={selectedProductLines}
          handleProductLineChange={handleProductLineChange}
          productionLineOptions={productLineOptions}
          selectedMachines={selectedMachines}
          handleMachineChange={handleMachineChange}
          machineOptions={machineOptions}
          selectedSensors={selectedSensors}
          handleSensorChange={handleSensorChange}
          sensorOptions={sensorOptions}
          filteredTreeData={filteredTreeData}
          selectedItems={selectedItems}
          handleSelectionChange={handleSelectionChange}
        />
      )}
      {activeStep === 1 && (
        <Step2
          handleInputChange={handleInputChange}
          templateIdentification={templateIdentification}
          errors={errors}
          filterTime={filterTime}
          setFilterTime={setFilterTime}
          periodObject={periodObject}
          description={description}
          accessType={accessType}
          setAccessType={setAccessType}
        />
      )}
    </Box>
  );

  return (
    <NewDialog
      open={open}
      handleClose={handleCloseDialog}
      handleCancel={handleCloseDialog}
      handleOk={handleCloseDialog}
      title={t('dialog.add_new_template.title')}
      subTitle={t('dialog.add_new_template.subtitle')}
      content={content}
      displayActionBar="flex"
      justifyContentActionBar="space-between"
      paddingXContent={{ sm: '1rem', md: '2rem', lg: '3rem' }}
      actionBar={
        <ActionBar
          handleCloseDialog={handleCloseDialog}
          activeStep={activeStep}
          handleBack={handleBack}
          steps={steps}
          handleSave={handleSave}
          handleNext={handleNext}
        />
      }
    />
  );
}

AddNewTemplate.propTypes = {
  open: T.bool.isRequired,
  handleCloseDialog: T.func.isRequired,
  machine: T.shape().isRequired
};
